import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NavController, ToastController } from '@ionic/angular/lazy';
import { ApiService } from './api.service';

export interface AppNotification {
    id: number;
    type: string;
    subject: string;
    body: string;
    link: string | null;
    read_at: string | null;
    created_at: string;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {

    private _unreadCount$ = new BehaviorSubject<number>(0);
    readonly unreadCount$ = this._unreadCount$.asObservable();

    private _refreshPromise: Promise<void> | null = null;
    private _lastFetchedAt = 0;
    private readonly _CACHE_TTL_MS = 30_000;

    constructor(
        private apiService: ApiService,
        private toastController: ToastController,
        private navController: NavController,
    ) {}

    async getNotifications(page = 1): Promise<{ data: AppNotification[]; meta: any }> {
        const resp: any = await this.apiService.doRequest('notifications', 'GET', { page });
        return resp?.data ?? { data: [], meta: {} };
    }

    async getUnreadCount(): Promise<number> {
        const resp: any = await this.apiService.doRequest('notifications/unread-count', 'GET', {});
        return resp?.data?.count ?? 0;
    }

    async refreshUnreadCount(force = false): Promise<void> {
        if (!force && Date.now() - this._lastFetchedAt < this._CACHE_TTL_MS) {
            return;
        }
        if (this._refreshPromise) {
            return this._refreshPromise;
        }
        this._refreshPromise = (async () => {
            try {
                const count = await this.getUnreadCount();
                this._unreadCount$.next(count);
                this._lastFetchedAt = Date.now();
            } catch {
                // keep existing value
            } finally {
                this._refreshPromise = null;
            }
        })();
        return this._refreshPromise;
    }

    async markAsRead(id: number): Promise<void> {
        await this.apiService.doRequest(`notifications/${id}/read`, 'POST', {});
        const current = this._unreadCount$.getValue();
        if (current > 0) {
            this._unreadCount$.next(current - 1);
        }
    }

    async consume(resourceType: string, resourceId: number): Promise<void> {
        try {
            const resp: any = await this.apiService.doRequest('consume', 'POST', {
                resource_type: resourceType,
                resource_id: resourceId,
            });

            const data = resp?.data ?? {};
            const unlocked: any[] = Array.isArray(data.unlocked) ? data.unlocked : [];
            const recommended: any[] = Array.isArray(data.recommended) ? data.recommended : [];

            if (unlocked.length || recommended.length) {
                await this.refreshUnreadCount(true);
                await this._showUnlockToast(unlocked, recommended);
            }
        } catch {
            // consume failures are non-critical
        }
    }

    private async _showUnlockToast(unlocked: any[], recommended: any[]): Promise<void> {
        const titlesOf = (items: any[]): string =>
            items.map((item) => item?.title).filter(Boolean).join(', ');

        const parts: string[] = [];

        if (unlocked.length) {
            parts.push(`You've unlocked: ${titlesOf(unlocked)}`);
        }

        if (recommended.length) {
            parts.push(`Recommended for you: ${titlesOf(recommended)}`);
        }

        const message = parts.join(' · ') || 'You\'ve unlocked a new resource!';

        const toast = await this.toastController.create({
            message,
            color: 'success',
            duration: 6000,
            cssClass: 'toast-success toast-unlock',
            buttons: [{
                text: 'View',
                role: 'cancel',
                handler: () => { this.navController.navigateForward(['/notifications']); },
            }],
        });
        await toast.present();
    }
}
